import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    CameraCreationItem,
    CameraCreationStatus,
    FileUploadItem,
    FileUploadStatus,
} from '@/types/source-creation';
import { SourceFolder, SourceFolderListResponse } from '@/types/source-folder';
import { SourcesSelection } from '@/types/source';
import { revalidateSourceFolders, useSourceFoldersInfinite } from '@/hooks';
import { toOrderByParam, type FolderSortOption } from './useFolderSort';

const FOLDER_PARAM = 'folder';
const PRIVATE_PARAM_VALUE = 'private';
const ORGANIZATION_PARAM_VALUE = 'organization';

const DEFAULT_SELECTION: SourcesSelection = { kind: 'private' };

function parseSelectionFromParam(value: string | null): SourcesSelection {
    if (value === ORGANIZATION_PARAM_VALUE) return { kind: 'organization' };
    if (value === PRIVATE_PARAM_VALUE || !value) return { kind: 'private' };
    return { kind: 'folder', id: value };
}

function dedupeFoldersById(
    pages: SourceFolderListResponse[] | undefined,
): SourceFolder[] {
    return Array.from(
        new Map(
            (pages ?? [])
                .flatMap((page) => page.items)
                .map((folder) => [folder.id, folder] as const),
        ).values(),
    );
}

function selectionToParam(selection: SourcesSelection): string | null {
    switch (selection.kind) {
        case 'private':
            return null;
        case 'organization':
            return ORGANIZATION_PARAM_VALUE;
        case 'folder':
            return selection.id;
    }
}

export function useFolderData(
    fileItems: FileUploadItem[],
    cameraItems: CameraCreationItem[],
    myFoldersSort: FolderSortOption,
    sharedFoldersSort: FolderSortOption,
) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [selection, setSelectionState] =
        useState<SourcesSelection>(DEFAULT_SELECTION);

    const setSelection = useCallback(
        (next: SourcesSelection) => {
            setSelectionState(next);
            setSearchParams(
                (prev) => {
                    const params = new URLSearchParams(prev);
                    const value = selectionToParam(next);
                    if (value) {
                        params.set(FOLDER_PARAM, value);
                    } else {
                        params.delete(FOLDER_PARAM);
                    }
                    return params;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    const {
        data: myFoldersPages,
        size: myFoldersSize,
        isLoading: myFoldersLoading,
        isValidating: myFoldersValidating,
        hasMore: hasMoreMyFolders,
        loadMore: loadMoreMyFolders,
    } = useSourceFoldersInfinite(
        false,
        { revalidateOnFocus: false },
        { ownership: 'owned', orderBy: toOrderByParam(myFoldersSort) },
    );

    const {
        data: sharedFoldersPages,
        size: sharedFoldersSize,
        isLoading: sharedFoldersLoading,
        isValidating: sharedFoldersValidating,
        hasMore: hasMoreSharedFolders,
        loadMore: loadMoreSharedFolders,
    } = useSourceFoldersInfinite(
        false,
        { revalidateOnFocus: false },
        { ownership: 'shared', orderBy: toOrderByParam(sharedFoldersSort) },
    );

    const myFolders = dedupeFoldersById(myFoldersPages);
    const sharedFolders = dedupeFoldersById(sharedFoldersPages);
    const sourceGroups: SourceFolder[] = [...myFolders, ...sharedFolders];

    const allSourcesCount = myFoldersPages?.[0]?.totalSourcesCount ?? 0;
    const sharedSourcesCount = sharedFoldersPages?.[0]?.totalSourcesCount ?? 0;

    const groupsLoading = myFoldersLoading || sharedFoldersLoading;
    const groupsValidating = myFoldersValidating || sharedFoldersValidating;
    const isLoadingMoreMyFolders = myFoldersValidating && myFoldersSize > 1;
    const isLoadingMoreSharedFolders =
        sharedFoldersValidating && sharedFoldersSize > 1;

    const completedFolderItemIds = fileItems
        .filter((item) => item.status === FileUploadStatus.COMPLETED)
        .map((item) => item.id)
        .join(',');

    useEffect(() => {
        if (!completedFolderItemIds) return;
        revalidateSourceFolders();
    }, [completedFolderItemIds]);

    const completedCameraItemIds = cameraItems
        .filter((item) => item.status === CameraCreationStatus.COMPLETED)
        .map((item) => item.id)
        .join(',');

    useEffect(() => {
        if (!completedCameraItemIds) return;
        revalidateSourceFolders();
    }, [completedCameraItemIds]);

    useEffect(() => {
        const next = parseSelectionFromParam(searchParams.get(FOLDER_PARAM));
        setSelectionState(next);
    }, [searchParams]);

    useEffect(() => {
        if (groupsLoading || !myFoldersPages || !sharedFoldersPages) return;
        if (selection.kind !== 'folder') return;
        const folderIds = new Set(sourceGroups.map((folder) => folder.id));
        if (folderIds.has(selection.id)) return;
        if (groupsValidating) return;
        // The selected folder may still be on an unfetched page of either
        // stream, so both must be exhausted before falling back.
        if (hasMoreMyFolders) {
            loadMoreMyFolders();
            return;
        }
        if (hasMoreSharedFolders) {
            loadMoreSharedFolders();
            return;
        }
        setSelection(DEFAULT_SELECTION);
    }, [
        groupsLoading,
        myFoldersPages,
        sharedFoldersPages,
        sourceGroups,
        selection,
        hasMoreMyFolders,
        hasMoreSharedFolders,
        groupsValidating,
        loadMoreMyFolders,
        loadMoreSharedFolders,
        setSelection,
    ]);

    return {
        selection,
        setSelection,
        sourceGroups,
        myFolders,
        sharedFolders,
        allSourcesCount,
        sharedSourcesCount,
        groupsLoading,
        hasMoreMyFolders,
        hasMoreSharedFolders,
        isLoadingMoreMyFolders,
        isLoadingMoreSharedFolders,
        loadMoreMyFolders,
        loadMoreSharedFolders,
    };
}
